/**
 * Schemas for the `outbreak.*` tRPC procedures.
 *
 * `outbreak.getCurrent` returns a fully hydrated payload — outbreak header,
 * counts (with citations), regions, timeline, advisories, and an optional
 * primary vessel. Composes the smaller per-procedure shapes from sibling
 * schema files so the contracts cannot drift.
 */

import { z } from "zod";

import {
  advisoryItemSchema,
} from "./advisories.js";
import { outbreakCountsSchema } from "./cases.js";
import { regionMarkerSchema } from "./regions.js";
import { timelineEventSchema } from "./timeline.js";

export const OUTBREAK_SYNDROMES = ["hps", "hfrs", "mixed", "unknown"] as const;
export const outbreakSyndromeSchema = z.enum(OUTBREAK_SYNDROMES);
export type OutbreakSyndrome = z.infer<typeof outbreakSyndromeSchema>;

export const OUTBREAK_STATUSES = [
  "emerging",
  "active",
  "monitored",
  "resolved",
] as const;
export const outbreakStatusSchema = z.enum(OUTBREAK_STATUSES);
export type OutbreakStatus = z.infer<typeof outbreakStatusSchema>;

export const OUTBREAK_RISK_LEVELS = [
  "very_high",
  "high",
  "moderate",
  "low",
] as const;
export const outbreakRiskLevelSchema = z.enum(OUTBREAK_RISK_LEVELS);
export type OutbreakRiskLevel = z.infer<typeof outbreakRiskLevelSchema>;

/**
 * Outbreak header used inside `outbreak.getCurrent` and `outbreak.list`.
 * Long-form fields are locale-resolved (one column per request).
 */
export const outbreakHeaderSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  virusStrain: z.string().nullable(),
  syndrome: outbreakSyndromeSchema,
  status: outbreakStatusSchema,
  riskLevelWho: outbreakRiskLevelSchema,
  startedAt: z.date(),
  resolvedAt: z.date().nullable(),
  /** Locale-resolved markdown summary. */
  summary: z.string().nullable(),
  populationAtRisk: z.number().int().nullable(),
  primaryCountry: z.string().length(2).nullable(),
  updatedAt: z.date(),
});
export type OutbreakHeader = z.infer<typeof outbreakHeaderSchema>;

export const primaryVesselSchema = z.object({
  mmsi: z.string(),
  name: z.string(),
});

export const outbreakGetCurrentOutputSchema = z.object({
  outbreak: outbreakHeaderSchema,
  counts: outbreakCountsSchema,
  regions: z.array(regionMarkerSchema),
  timeline: z.array(timelineEventSchema),
  advisories: z.array(advisoryItemSchema),
  primaryVessel: primaryVesselSchema.nullable(),
});
export type OutbreakGetCurrentOutput = z.infer<
  typeof outbreakGetCurrentOutputSchema
>;

export const outbreakGetRiskLevelOutputSchema = z.object({
  riskLevelWho: outbreakRiskLevelSchema,
  status: outbreakStatusSchema,
});

export const outbreakBySlugInputSchema = z.object({
  slug: z.string().min(1).max(100),
});

export const outbreakListInputSchema = z.object({
  status: outbreakStatusSchema.optional(),
  limit: z.number().int().min(1).max(50).default(20),
});

export const outbreakListOutputSchema = z.array(outbreakHeaderSchema);
